import React, { useEffect, useMemo, useState } from 'react';
import { useGetPickListPreviewQuery, useGetSurveyStonesQuery } from 'state';
import { useDebouncedValue } from 'hooks';
import { Skeleton } from 'components/_shared/Skeleton';
import { Button } from 'components/_shared/Button';
import {
  CAP_MIN,
  CAP_STEP,
  PICK_LIST_SWEET_SPOT,
  MUST_PICK_COLOR,
  OPTIONAL_PICK_COLOR
} from '../../fields.constants';
import {
  pickListCapMax,
  capForRequest,
  pickListSentence,
  sizeGauges,
  gaugeCaption,
  mustPickOverRate,
  rateEffect,
  formatCount
} from '../../fields.utils';
import {
  Card,
  Title,
  Group,
  Label,
  Gauges,
  Gauge,
  GaugeLabel,
  GaugeTrack,
  GaugeFill,
  GaugeNote,
  Warning,
  RateValue,
  SliderWrap,
  Range,
  SweetSpot,
  RateEnds,
  RateEffect,
  Result,
  Count,
  Caption,
  Split,
  SplitItem,
  Sentence,
  Tone,
  Message
} from './PickList.styles';
import { MustPickCmField } from './MustPickCmField';
import { useMustPickCm } from './useMustPickCm';

const PREVIEW_DELAY_MS = 250;

const Parts = ({ parts }) =>
  parts.map(({ text, tone }, index) =>
    tone ? (
      <Tone key={index} $tone={tone}>
        {text}
      </Tone>
    ) : (
      text
    )
  );

export const PickList = ({
  fieldId,
  surveyId,
  mustPickCm,
  stoneCount,
  acres,
  rule,
  onChangeRule,
  onListChange
}) => {
  const capMax = pickListCapMax(stoneCount, acres);
  const cap = Math.min(rule.cap, capMax);
  const capPerAcre = capForRequest(cap, capMax);

  const { isSaving, ...mustPickField } = useMustPickCm({ fieldId, surveyId, mustPickCm });

  const request = useMemo(() => ({ capPerAcre, mustPickCm }), [capPerAcre, mustPickCm]);
  const debounced = useDebouncedValue(request, PREVIEW_DELAY_MS);
  const { currentData, isFetching, isError, refetch } = useGetPickListPreviewQuery(
    { fieldId, ...debounced },
    { skip: stoneCount === 0, refetchOnMountOrArgChange: true }
  );
  const { data: stones } = useGetSurveyStonesQuery(surveyId, { skip: stoneCount === 0 });

  const isStale = isFetching || debounced !== request || isSaving;

  const [shown, setShown] = useState(null);
  useEffect(() => {
    if (currentData) setShown({ preview: currentData, ...debounced });
  }, [currentData]); // eslint-disable-line react-hooks/exhaustive-deps

  const emptyPreview = {
    matched: 0,
    selected: 0,
    held_back: 0,
    must_pick: 0,
    must_pick_cm: mustPickCm,
    area_acres: acres
  };
  const current = shown?.preview.survey_id === surveyId ? shown : null;
  const preview = stoneCount === 0 ? emptyPreview : current?.preview;

  useEffect(() => {
    onListChange(
      current && {
        surveyId,
        stoneIds: current.preview.stone_ids,
        selected: current.preview.selected,
        mustPick: current.preview.must_pick,
        mustPickCm: current.preview.must_pick_cm,
        capPerAcre: current.capPerAcre,
        capMax,
        gpx: {
          overLimit: current.preview.gpx_over_limit,
          maxWaypoints: current.preview.gpx_max_waypoints,
          maxCapPerAcre: current.preview.gpx_max_cap_per_acre
        },
        isStale
      }
    );
  }, [current, surveyId, isStale, capMax, onListChange]);

  const listedIds = useMemo(
    () => current && new Set(current.preview.stone_ids),
    [current]
  );
  const gauges = useMemo(
    () => listedIds && sizeGauges({ stones, selectedIds: listedIds }),
    [stones, listedIds]
  );
  const overRate =
    current &&
    mustPickOverRate({ preview: current.preview, capPerAcre: current.capPerAcre });

  const renderResult = () => {
    if (isError && !preview) {
      return (
        <Message>
          Could not load the pick list.
          <Button onClick={refetch}>Retry</Button>
        </Message>
      );
    }
    if (!preview) return <Skeleton height='120px' />;

    return (
      <Result $stale={isFetching} data-testid='pick-list-result'>
        <Count>{formatCount(preview.selected)}</Count>
        <Caption>
          stones in the draft list, over {Math.round(preview.area_acres)} acres
        </Caption>
        <Split>
          <SplitItem $color={MUST_PICK_COLOR}>
            <i />
            {formatCount(preview.must_pick)} red
          </SplitItem>
          <SplitItem $color={OPTIONAL_PICK_COLOR}>
            <i />
            {formatCount(preview.selected - preview.must_pick)} yellow
          </SplitItem>
        </Split>
        <Sentence data-testid='pick-list-sentence'>
          <Parts
            parts={pickListSentence({
              preview,
              capPerAcre: current?.capPerAcre ?? null,
              stoneCount
            })}
          />
        </Sentence>
      </Result>
    );
  };

  const sweetFraction = (PICK_LIST_SWEET_SPOT - CAP_MIN) / (capMax - CAP_MIN);
  const showSweetSpot = PICK_LIST_SWEET_SPOT > CAP_MIN && PICK_LIST_SWEET_SPOT < capMax;

  return (
    <Card data-testid='pick-list'>
      <Title>Pick list</Title>
      <MustPickCmField {...mustPickField} />
      {gauges && (
        <Group>
          <Label>On the pick list, by size</Label>
          <Gauges data-testid='pick-list-gauges'>
            {gauges.map(({ key, label, color, total, onList }) => (
              <Gauge
                key={key}
                title={total ? `${onList} of ${total} on the pick list` : undefined}
              >
                <GaugeLabel>{label}</GaugeLabel>
                <GaugeTrack
                  role='img'
                  aria-label={`${label}: ${onList} of ${total} on the pick list`}
                >
                  <GaugeFill $color={color} $fraction={total ? onList / total : 0} />
                </GaugeTrack>
                <GaugeNote>{gaugeCaption({ total, onList })}</GaugeNote>
              </Gauge>
            ))}
          </Gauges>
        </Group>
      )}
      <Group>
        <Label>
          Desired removal rate
          <RateValue data-testid='pick-list-rate'>
            {capPerAcre === null
              ? `everything (${capMax.toFixed(2)} /ac)`
              : `${cap.toFixed(2)} /ac`}
          </RateValue>
        </Label>
        <SliderWrap>
          <Range
            type='range'
            aria-label='Desired removal rate'
            min={CAP_MIN}
            max={capMax}
            step={CAP_STEP}
            value={cap}
            onChange={event => onChangeRule({ ...rule, cap: Number(event.target.value) })}
          />
          {showSweetSpot && (
            <SweetSpot
              $fraction={sweetFraction}
              title={`Around ${PICK_LIST_SWEET_SPOT.toFixed(
                2
              )}/ac is where picking goes fastest`}
              data-testid='pick-list-sweet-spot'
            >
              <span />
              {PICK_LIST_SWEET_SPOT.toFixed(2)}
            </SweetSpot>
          )}
        </SliderWrap>
        <RateEnds>
          <span>{CAP_MIN.toFixed(2)}</span>
          <span>{capMax.toFixed(2)} /ac (all)</span>
        </RateEnds>
        {overRate && <Warning data-testid='pick-list-over-rate'>{overRate}</Warning>}
        {preview && (stoneCount === 0 || gauges) && (
          <RateEffect data-testid='pick-list-effect'>
            <Parts parts={rateEffect({ preview, gauges, stoneCount })} />
          </RateEffect>
        )}
      </Group>
      {renderResult()}
    </Card>
  );
};
